/**
 * The pages of the public site, and where each one is edited.
 *
 * This is an inventory, not a content store. The Website Content screen in the
 * admin CRM reads it to list pages and to point Edit at an editor that already
 * exists. Nothing here saves anything, and there is deliberately no draft model:
 * the Community editor and the event editor keep their own save paths.
 *
 * A page with `editor: null` is written in code. It is listed so an admin can
 * see that it exists and why there is no Edit button, instead of wondering.
 */

/** Admin workspace views a page can be edited from. Must exist in AdminCRM. */
export type WebsiteContentEditorView = "community-hero" | "events" | "speakers-featured";

export type WebsiteContentEditor = {
  view: WebsiteContentEditorView;
  /** What the admin sees on the button, e.g. "Edit in Community hero". */
  label: string;
  /** The server procedures the editor saves through. For documentation and tests. */
  saves: readonly string[];
};

export type WebsiteContentAudience = "public" | "members" | "legal";

export type WebsiteContentPage = {
  id: string;
  title: string;
  /** The live path, with `:slug` style params left in. */
  path: string;
  /** A concrete path the admin can open, when `path` has a param. */
  livePath: string;
  audience: WebsiteContentAudience;
  /** What on the page an admin can change. */
  editable: string;
  editor: WebsiteContentEditor | null;
};

const COMMUNITY_EDITOR: WebsiteContentEditor = {
  view: "community-hero",
  label: "Edit in Community hero",
  saves: ["community.saveHero", "community.savePage"],
};

const EVENTS_EDITOR: WebsiteContentEditor = {
  view: "events",
  label: "Edit in Events",
  saves: ["events.create", "events.update"],
};

export const WEBSITE_CONTENT_PAGES: readonly WebsiteContentPage[] = [
  {
    id: "home",
    title: "Home",
    path: "/",
    livePath: "/",
    audience: "public",
    editable: "Featured event and display mode are managed above. Hero artwork and community-story content remain in code.",
    editor: null,
  },
  {
    id: "mission",
    title: "Mission and values",
    path: "/mission-and-values",
    livePath: "/mission-and-values",
    audience: "public",
    editable: "Written in code.",
    editor: null,
  },
  {
    id: "community",
    title: "Community",
    path: "/community",
    livePath: "/community",
    audience: "members",
    editable: "Hero, member and event spotlights, sponsor strip, news cards, categories and sidebar.",
    editor: COMMUNITY_EDITOR,
  },
  {
    id: "calendar",
    title: "Calendar of events",
    path: "/calendar-of-events",
    livePath: "/calendar-of-events",
    audience: "public",
    editable: "The list is the events you publish, in Events.",
    editor: EVENTS_EDITOR,
  },
  {
    id: "event-detail",
    title: "Event page",
    path: "/events/:slug",
    livePath: "/calendar-of-events",
    audience: "public",
    editable: "Title, date, description, speakers, art, registration type and pricing, per event.",
    editor: EVENTS_EDITOR,
  },
  {
    id: "speakers",
    title: "Featured speakers",
    path: "/speakers",
    livePath: "/speakers",
    audience: "public",
    editable: "Which speakers are featured, their order and their card headline.",
    editor: {
      view: "speakers-featured",
      label: "Edit featured speakers",
      saves: ["speakers.featured.create", "speakers.featured.update", "speakers.featured.remove"],
    },
  },
  {
    id: "sponsor",
    title: "Sponsor page",
    path: "/sponsors/:slug",
    livePath: "/community",
    audience: "public",
    editable: "Sponsor profiles are written in code. Which sponsors the Community page shows is set in Community hero.",
    editor: COMMUNITY_EDITOR,
  },
  {
    id: "collaborate",
    title: "Collaborate with us",
    path: "/collaborate-with-us",
    livePath: "/collaborate-with-us",
    audience: "public",
    editable: "Written in code.",
    editor: null,
  },
  {
    id: "premium",
    title: "Membership",
    path: "/membership",
    livePath: "/membership",
    audience: "public",
    editable: "Written in code. The price is set in Settings.",
    editor: null,
  },
  {
    id: "privacy",
    title: "Privacy policy",
    path: "/privacy-policy",
    livePath: "/privacy-policy",
    audience: "legal",
    editable: "Written in code, so a change goes through review.",
    editor: null,
  },
  {
    id: "disclaimer",
    title: "Disclaimer",
    path: "/disclaimer",
    livePath: "/disclaimer",
    audience: "legal",
    editable: "Written in code, so a change goes through review.",
    editor: null,
  },
];
